import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../services/api";
import { BRAND } from "../../branding";
import grids from "../responsive-fixes/grids.module.css";

// Local substitutes for audit-branch components (don't exist in this repo)
const DESIGN = {
  colors: { text: "#dae2fd", glass: "rgba(255,255,255,0.04)", border: "rgba(200,191,255,0.15)" },
  borderRadius: { small: 8, medium: 10, large: 14 },
  fontFamily: "'Space Grotesk', sans-serif",
  styles: {
    label: { fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, display: "block", color: "#4a6070" },
    input: { width: "100%", boxSizing: "border-box" as const, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,191,255,0.15)", borderRadius: 9, padding: "9px 14px", color: "#dae2fd", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13 },
  },
};
const LoadingSpinner: React.FC<{size?: string}> = ({size}) => <span style={{opacity:0.6}}>{size === "small" ? "..." : "Loading..."}</span>;
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean}> {
  constructor(props: any) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() { return this.state.hasError ? <div>Error loading component.</div> : this.props.children; }
}
interface CrawlJob {
  id: number;
  url: string;
  max_depth: number;
  extract_type: string;
  status: "pending" | "running" | "completed" | "failed" | "cancelled";
  pages_crawled: number;
  created_at: string;
  completed_at: string | null;
  tags: string[];
}

interface CrawlResult {
  id: number;
  job_id: number;
  page_url: string;
  extracted_data: any;
  extracted_at: string;
  has_error?: boolean;
  error_message?: string;
}

const EXTRACT_TYPES = [
  { value: "products", label: "Products", icon: "ðŸ“¦", color: "#ffd6a5" },
  { value: "contacts", label: "Contacts", icon: "ðŸ‘¥", color: "#a5d6ff" },
  { value: "leads", label: "Leads", icon: "ðŸŽ¯", color: "#b8d58d" },
  { value: "prices", label: "Prices", icon: "ðŸ’°", color: "#ffb6c1" },
  { value: "general", label: "General", icon: "ðŸ“Š", color: "#c8bfff" },
];

const STATUS_CONFIG: Record<string, { color: string; bg: string; icon: string }> = {
  pending: { color: "#ffd6a5", bg: "rgba(255,214,165,0.1)", icon: "â³" },
  running: { color: "#4f6ef7", bg: "rgba(79,110,247,0.1)", icon: "ðŸ”„" },
  completed: { color: "#b8d58d", bg: "rgba(184,213,141,0.1)", icon: "âœ…" },
  failed: { color: "#ff6b6b", bg: "rgba(255,107,107,0.1)", icon: "âŒ" },
  cancelled: { color: "#4a6070", bg: "rgba(74,96,112,0.1)", icon: "ðŸš«" },
};

const VeridianCrawlerDashboard: React.FC = () => {
  // State
  const [jobs, setJobs] = useState<CrawlJob[]>([]);
  const [results, setResults] = useState<CrawlResult[]>([]);
  const [selectedJob, setSelectedJob] = useState<CrawlJob | null>(null);
  const [loading, setLoading] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [veridianStatus, setVeridianStatus] = useState<string>("checking");
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [url, setUrl] = useState("");
  const [depth, setDepth] = useState(2);
  const [extractType, setExtractType] = useState("products");
  const [tags, setTags] = useState<string[]>([]);

  // Poll for jobs every 10 seconds (less aggressive)
  const fetchJobs = useCallback(async () => {
    try {
      const data = await api.crawler.jobs();
      setJobs(data || []);
      setError(null);
    } catch (err: any) {
      console.error("Failed to fetch jobs:", err);
      setError("Failed to load jobs");
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const status = await api.crawler.status();
      setVeridianStatus(status?.veridian || "unknown");
    } catch {
      setVeridianStatus("unreachable");
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    fetchStatus();
    const interval = setInterval(() => {
      fetchJobs();
      fetchStatus();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchJobs, fetchStatus]);

  // Handlers
  const fetchResults = async (job: CrawlJob) => {
    setSelectedJob(job);
    setLoading(true);
    setError(null);
    try {
      const data = await api.crawler.results(job.id);
      setResults(data || []);
    } catch (err: any) {
      setError(`Failed to load results: ${err.message}`);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const submitJob = async () => {
    if (!url.trim()) {
      setError("Please enter a URL");
      return;
    }
    
    try {
      new URL(url); // Validate URL format
    } catch {
      setError("Please enter a valid URL");
      return;
    }

    setSubmitting(true);
    setError(null);
    
    try {
      await api.crawler.submitJob({
        url: url.trim(),
        max_depth: depth,
        extract_type: extractType,
        tags,
      });
      
      setUrl("");
      setTags([]);
      await fetchJobs();
    } catch (err: any) {
      setError(`Failed to submit job: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const cancelJob = async (id: number) => {
    try {
      await api.crawler.cancelJob(id);
      await fetchJobs();
      if (selectedJob?.id === id) {
        setSelectedJob(null);
        setResults([]);
      }
    } catch (err: any) {
      setError(`Failed to cancel job: ${err.message}`);
    }
  };

  const deleteJob = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this job and all its results?")) {
      return;
    }
    
    try {
      await api.crawler.deleteJob(id);
      await fetchJobs();
      if (selectedJob?.id === id) {
        setSelectedJob(null);
        setResults([]);
      }
    } catch (err: any) {
      setError(`Failed to delete job: ${err.message}`);
    }
  };

  const addTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && e.currentTarget.value.trim()) {
      const newTag = e.currentTarget.value.trim();
      if (!tags.includes(newTag) && tags.length < 5) {
        setTags([...tags, newTag]);
      }
      e.currentTarget.value = "";
    }
  };

  // Computed values
  const runningCount = jobs.filter(j => j.status === "running").length;
  const completedCount = jobs.filter(j => j.status === "completed").length;
  const failedCount = jobs.filter(j => j.status === "failed").length;

  return (
    <ErrorBoundary>
      <div style={{ padding: "24px", fontFamily: DESIGN.fontFamily, color: DESIGN.colors.text }}>
        {/* Error Display */}
        {error && (
          <div style={{
            background: "rgba(255,107,107,0.1)",
            border: "1px solid rgba(255,107,107,0.3)",
            borderRadius: DESIGN.borderRadius.medium,
            padding: "12px 16px",
            marginBottom: 20,
            color: "#ff6b6b",
            fontSize: 13,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <span>{error}</span>
            <button 
              onClick={() => setError(null)}
              style={{ background: "none", border: "none", color: "#ff6b6b", cursor: "pointer" }}
            >
              âœ•
            </button>
          </div>
        )}

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
          <div style={{
            width: 56, height: 56,
            background: "linear-gradient(135deg, rgba(79,110,247,0.2), rgba(200,191,255,0.1))",
            border: "1px solid rgba(79,110,247,0.3)",
            borderRadius: DESIGN.borderRadius.large,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 28,
          }}>
            ðŸ•·ï¸
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{ 
              margin: 0, fontSize: 24, fontWeight: 800,
              fontFamily: "'Manrope', sans-serif", color: "#ffd6a5" 
            }}>
              Veridian Crawler
            </h1>
            <p style={{ margin: 0, fontSize: 13, color: "#4a6a7a" }}>
              AI-powered BFS web crawler â€” extract products, contacts, leads & prices
            </p>
          </div>
          
          {/* Status Badges */}
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{
              background: veridianStatus === "ok" 
                ? "rgba(184,213,141,0.1)" 
                : "rgba(255,107,107,0.1)",
              border: `1px solid ${veridianStatus === "ok" 
                ? "rgba(184,213,141,0.3)" 
                : "rgba(255,107,107,0.3)"}`,
              borderRadius: DESIGN.borderRadius.medium,
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 700,
              color: veridianStatus === "ok" ? "#b8d58d" : "#ff6b6b",
            }}>
              {veridianStatus === "checking" ? "â³ Checking..." : 
               veridianStatus === "ok" ? "ðŸ§ž Veridian Ready" : "âš ï¸ Veridian Offline"}
            </div>
            
            {runningCount > 0 && (
              <div style={{
                background: "rgba(79,110,247,0.1)",
                border: "1px solid rgba(79,110,247,0.3)",
                borderRadius: DESIGN.borderRadius.medium,
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 700,
                color: "#4f6ef7",
              }}>
                ðŸ”„ {runningCount} Running
              </div>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(4, 1fr)", 
          gap: 16, 
          marginBottom: 24 
        }}>
          {[
            { label: "Total Jobs", value: jobs.length, color: "#dae2fd", icon: "ðŸ“Š" },
            { label: "Running", value: runningCount, color: "#4f6ef7", icon: "ðŸ”„" },
            { label: "Completed", value: completedCount, color: "#b8d58d", icon: "âœ…" },
            { label: "Failed", value: failedCount, color: "#ff6b6b", icon: "âŒ" },
          ].map(stat => (
            <div key={stat.label} style={{
              background: DESIGN.colors.glass,
              backdropFilter: "blur(60px)",
              border: `1px solid ${DESIGN.colors.border}`,
              borderRadius: DESIGN.borderRadius.large,
              padding: 20,
              textAlign: "center",
            }}>
              <div style={{ fontSize: 14, marginBottom: 8 }}>{stat.icon}</div>
              <div style={{ 
                fontSize: 28, fontWeight: 800, color: stat.color,
                fontFamily: "'Manrope', sans-serif" 
              }}>
                {stat.value}
              </div>
              <div style={{ fontSize: 12, color: "#3a5060", marginTop: 4 }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.8fr", gap: 20 }}>
          {/* Left Panel - New Job Form */}
          <div>
            <div style={{
              background: DESIGN.colors.glass,
              backdropFilter: "blur(60px)",
              border: `1px solid ${DESIGN.colors.border}`,
              borderRadius: DESIGN.borderRadius.large,
              padding: 24,
              marginBottom: 20,
            }}>
              <h3 style={{ margin: "0 0 20px", fontSize: 16, fontWeight: 700, color: "#c8bfff" }}>
                ðŸš€ New Crawl Job
              </h3>
              
              {/* URL Input */}
              <div style={{ marginBottom: 16 }}>
                <label style={DESIGN.styles.label}>Target URL</label>
                <input
                  style={DESIGN.styles.input}
                  placeholder="https://example.com"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !submitting && submitJob()}
                />
              </div>

              {/* Extract Type Selector */}
              <div style={{ marginBottom: 16 }}>
                <label style={DESIGN.styles.label}>Extract Type</label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                  {EXTRACT_TYPES.slice(0, 3).map(type => (
                    <button
                      key={type.value}
                      onClick={() => setExtractType(type.value)}
                      style={{
                        background: extractType === type.value 
                          ? `${type.color}20` 
                          : "rgba(255,255,255,0.03)",
                        border: `1px solid ${extractType === type.value 
                          ? `${type.color}60` 
                          : "rgba(200,191,255,0.15)"}`,
                        borderRadius: DESIGN.borderRadius.medium,
                        padding: "12px 8px",
                        color: extractType === type.value ? type.color : "#9ab0c9",
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 600,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <span style={{ fontSize: 16 }}>{type.icon}</span>
                      {type.label}
                    </button>
                  ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginTop: 8 }}>
                  {EXTRACT_TYPES.slice(3).map(type => (
                    <button
                      key={type.value}
                      onClick={() => setExtractType(type.value)}
                      style={{
                        background: extractType === type.value 
                          ? `${type.color}20` 
                          : "rgba(255,255,255,0.03)",
                        border: `1px solid ${extractType === type.value 
                          ? `${type.color}60` 
                          : "rgba(200,191,255,0.15)"}`,
                        borderRadius: DESIGN.borderRadius.medium,
                        padding: "12px 8px",
                        color: extractType === type.value ? type.color : "#9ab0c9",
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                      }}
                    >
                      <span style={{ fontSize: 16 }}>{type.icon}</span>
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Depth Selector */}
              <div style={{ marginBottom: 16 }}>
                <label style={DESIGN.styles.label}>Max Depth</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {[1, 2, 3, 4, 5].map(d => (
                    <button
                      key={d}
                      onClick={() => setDepth(d)}
                      style={{
                        flex: 1,
                        background: depth === d 
                          ? "rgba(79,110,247,0.2)" 
                          : "rgba(255,255,255,0.03)",
                        border: `1px solid ${depth === d 
                          ? "rgba(79,110,247,0.5)" 
                          : "rgba(200,191,255,0.15)"}`,
                        borderRadius: DESIGN.borderRadius.medium,
                        padding: "10px",
                        color: depth === d ? "#4f6ef7" : "#9ab0c9",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tags Input */}
              <div style={{ marginBottom: 20 }}>
                <label style={DESIGN.styles.label}>Tags (press Enter to add)</label>
                <input
                  style={DESIGN.styles.input}
                  placeholder="Add tag..."
                  onKeyDown={addTag}
                />
                {tags.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                    {tags.map(tag => (
                      <span key={tag} style={{
                        background: "rgba(79,110,247,0.1)",
                        border: "1px solid rgba(79,110,247,0.3)",
                        borderRadius: 20,
                        padding: "4px 10px",
                        fontSize: 11,
                        color: "#4f6ef7",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}>
                        {tag}
                        <button 
                          onClick={() => setTags(tags.filter(t => t !== tag))}
                          style={{ 
                            background: "none", border: "none", 
                            color: "#4f6ef7", cursor: "pointer", padding: 0,
                            fontSize: 12 
                          }}
                        >
                          Ã—
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                onClick={submitJob}
                disabled={submitting || !url.trim()}
                style={{
                  width: "100%",
                  padding: "14px",
                  background: submitting || !url.trim()
                    ? "rgba(79,110,247,0.1)"
                    : "linear-gradient(135deg, rgba(79,110,247,0.3), rgba(200,191,255,0.15))",
                  border: "1px solid rgba(79,110,247,0.4)",
                  borderRadius: DESIGN.borderRadius.medium,
                  color: submitting || !url.trim() ? "#3a5060" : "#c8bfff",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: submitting || !url.trim() ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                {submitting ? (
                  <>
                    <LoadingSpinner size="small" />
                    Submitting...
                  </>
                ) : (
                  "â–¶ Start Crawl"
                )}
              </button>
            </div>

            {/* Extract Type Guide */}
            <div style={{
              background: DESIGN.colors.glass,
              backdropFilter: "blur(60px)",
              border: `1px solid ${DESIGN.colors.border}`,
              borderRadius: DESIGN.borderRadius.large,
              padding: 20,
              fontSize: 12,
              color: "#4a6070",
              lineHeight: 1.8,
            }}>
              <div style={{ fontWeight: 700, color: "#9ab0c9", marginBottom: 12, fontSize: 13 }}>
                Extraction Guide
              </div>
              {EXTRACT_TYPES.map(type => (
                <div key={type.value} style={{ marginBottom: 8, display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontSize: 14 }}>{type.icon}</span>
                  <div>
                    <div style={{ color: type.color, fontWeight: 600 }}>{type.label}</div>
                    <div style={{ fontSize: 11, color: "#3a5060" }}>
                      {type.value === "products" && "SKU, price, title, images, inventory"}
                      {type.value === "contacts" && "Name, email, phone, company, role"}
                      {type.value === "leads" && "Business leads, decision makers, contact info"}
                      {type.value === "prices" && "Competitor pricing, market intelligence"}
                      {type.value === "general" && "All content, structured metadata"}
                    </div>
                  </div>
                </div>
              ))}
              
              <div style={{ 
                marginTop: 16, paddingTop: 16, 
                borderTop: "1px solid rgba(78,69,59,0.2)",
                display: "flex", alignItems: "center", gap: 8 
              }}>
                <img 
                  src="/brand/veridian-logo.png" 
                  alt="Veridian" 
                  style={{ width: 16, height: 16, objectFit: "contain" }} 
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                <span>Powered by Veridian Ginie â€” HuggingFace Qwen2.5-72B</span>
              </div>
            </div>
          </div>

          {/* Right Panel - Jobs Table */}
          <div style={{
            background: DESIGN.colors.glass,
            backdropFilter: "blur(60px)",
            border: `1px solid ${DESIGN.colors.border}`,
            borderRadius: DESIGN.borderRadius.large,
            padding: 24,
          }}>
            <div style={{ 
              display: "flex", justifyContent: "space-between", 
              alignItems: "center", marginBottom: 20 
            }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#dae2fd" }}>
                ðŸ“‹ Crawl Jobs
              </h3>
              <button
                onClick={fetchJobs}
                style={{
                  padding: "8px 16px",
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(200,191,255,0.15)",
                  borderRadius: DESIGN.borderRadius.medium,
                  color: "#9ab0c9",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                â†º Refresh
              </button>
            </div>

            {jobsLoading ? (
              <div style={{ 
                textAlign: "center", padding: "60px 0", 
                color: "#4a6070" 
              }}>
                <LoadingSpinner />
                <div style={{ marginTop: 12 }}>Loading jobs...</div>
              </div>
            ) : jobs.length === 0 ? (
              <div style={{ 
                textAlign: "center", color: "#2a3a4a", 
                padding: "60px 0", fontSize: 14 
              }}>
                No crawl jobs yet. Submit one to get started.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["ID", "URL", "Type", "Pages", "Status", "Actions"].map(header => (
                        <th key={header} style={{
                          padding: "12px 16px",
                          textAlign: "left",
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                          color: "#3a5060",
                          textTransform: "uppercase",
                          borderBottom: "1px solid rgba(78,69,59,0.2)",
                        }}>
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map(job => {
                      const statusConfig = STATUS_CONFIG[job.status];
                      const isSelected = selectedJob?.id === job.id;
                      
                      return (
                        <tr 
                          key={job.id}
                          onClick={() => fetchResults(job)}
                          style={{ 
                            cursor: "pointer",
                            background: isSelected 
                              ? "rgba(79,110,247,0.07)" 
                              : "transparent",
                            transition: "background 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              (e.currentTarget as HTMLTableRowElement).style.background = 
                                "rgba(255,255,255,0.02)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              (e.currentTarget as HTMLTableRowElement).style.background = 
                                "transparent";
                            }
                          }}
                        >
                          <td style={{ 
                            padding: "12px 16px", fontSize: 12, 
                            color: "#4a6070", fontWeight: 600 
                          }}>
                            #{job.id}
                          </td>
                          <td style={{ 
                            padding: "12px 16px", fontSize: 12, 
                            maxWidth: 200, overflow: "hidden", 
                            textOverflow: "ellipsis", whiteSpace: "nowrap" 
                          }}>
                            <span 
                              title={job.url} 
                              style={{ color: "#c8bfff" }}
                            >
                              {job.url.replace(/^https?:\/\//, "")}
                            </span>
                          </td>
                          <td style={{ padding: "12px 16px", fontSize: 12, color: "#9ab0c9" }}>
                            {job.extract_type}
                          </td>
                          <td style={{ 
                            padding: "12px 16px", fontSize: 12, 
                            textAlign: "center", color: "#9ab0c9" 
                          }}>
                            {job.pages_crawled}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <span style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: "4px 10px",
                              borderRadius: 6,
                              background: statusConfig.bg,
                              border: `1px solid ${statusConfig.color}40`,
                              color: statusConfig.color,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}>
                              {statusConfig.icon} {job.status}
                            </span>
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <div style={{ display: "flex", gap: 6 }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  fetchResults(job);
                                }}
                                style={{
                                  padding: "6px 12px",
                                  background: "rgba(79,110,247,0.1)",
                                  border: "1px solid rgba(79,110,247,0.3)",
                                  borderRadius: 6,
                                  color: "#4f6ef7",
                                  fontSize: 11,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                }}
                              >
                                View
                              </button>
                              
                              {(job.status === "pending" || job.status === "running") && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    cancelJob(job.id);
                                  }}
                                  style={{
                                    padding: "6px 12px",
                                    background: "rgba(255,165,0,0.1)",
                                    border: "1px solid rgba(255,165,0,0.3)",
                                    borderRadius: 6,
                                    color: "#ffa500",
                                    fontSize: 11,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                  }}
                                >
                                  Stop
                                </button>
                              )}
                              
                              {job.status !== "running" && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteJob(job.id);
                                  }}
                                  style={{
                                    padding: "6px 12px",
                                    background: "rgba(255,107,107,0.1)",
                                    border: "1px solid rgba(255,107,107,0.3)",
                                    borderRadius: 6,
                                    color: "#ff6b6b",
                                    fontSize: 11,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                  }}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Results Panel */}
        {selectedJob && (
          <div style={{
            background: DESIGN.colors.glass,
            backdropFilter: "blur(60px)",
            border: `1px solid ${DESIGN.colors.border}`,
            borderRadius: DESIGN.borderRadius.large,
            padding: 24,
            marginTop: 20,
          }}>
            <div style={{ 
              display: "flex", justifyContent: "space-between", 
              alignItems: "center", marginBottom: 20 
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#dae2fd" }}>
                  ðŸ” Results â€” Job #{selectedJob.id}
                </h3>
                <div style={{ fontSize: 13, color: "#4a6070", marginTop: 4 }}>
                  {selectedJob.url} â€¢ {selectedJob.pages_crawled} pages â€¢ {selectedJob.extract_type}
                </div>
              </div>
              <button
                onClick={() => { setSelectedJob(null); setResults([]); }}
                style={{
                  padding: "8px 16px",
                  background: "rgba(255,107,107,0.1)",
                  border: "1px solid rgba(255,107,107,0.3)",
                  borderRadius: DESIGN.borderRadius.medium,
                  color: "#ff6b6b",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                âœ• Close
              </button>
            </div>

            {loading ? (
              <div style={{ 
                textAlign: "center", padding: "60px 0", 
                color: "#4a6070" 
              }}>
                <LoadingSpinner />
                <div style={{ marginTop: 12 }}>Loading results...</div>
              </div>
            ) : results.length === 0 ? (
              <div style={{ 
                textAlign: "center", color: "#2a3a4a", 
                padding: "60px 0", fontSize: 14 
              }}>
                {selectedJob.status === "completed" 
                  ? "No structured data extracted." 
                  : "Job still in progress â€” check back soon."}
              </div>
            ) : (
              // M2 fix (audit): results grid 320px clipped at <320px viewport.
              <div className={grids["responsive-grid"]} style={{ gap: 16 }}>
                {results.map(result => (
                  <div
                    key={result.id}
                    style={{
                      background: result.has_error 
                        ? "rgba(255,107,107,0.05)" 
                        : "rgba(255,255,255,0.02)",
                      border: `1px solid ${result.has_error 
                        ? "rgba(255,107,107,0.2)" 
                        : "rgba(78,69,59,0.15)"}`,
                      borderRadius: DESIGN.borderRadius.medium,
                      padding: 16,
                    }}
                  >
                    <div style={{ 
                      fontSize: 11, color: "#4a6070", marginBottom: 8,
                      overflow: "hidden", textOverflow: "ellipsis", 
                      whiteSpace: "nowrap", display: "flex", 
                      alignItems: "center", gap: 6 
                    }}>
                      {result.has_error ? "âš ï¸" : "ðŸ”—"} {result.page_url}
                    </div>
                    <pre style={{
                      margin: 0,
                      fontSize: 11,
                      color: result.has_error ? "#ff6b6b" : "#9ab0c9",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      maxHeight: 200,
                      overflowY: "auto",
                      fontFamily: "monospace",
                      background: "rgba(0,0,0,0.1)",
                      borderRadius: 6,
                      padding: 12,
                    }}>
                      {result.has_error 
                        ? result.error_message || "Extraction error"
                        : typeof result.extracted_data === "string"
                          ? result.extracted_data
                          : JSON.stringify(result.extracted_data, null, 2)}
                    </pre>
                    <div style={{ 
                      fontSize: 10, color: "#3a5060", marginTop: 8,
                      textAlign: "right" 
                    }}>
                      {result.extracted_at && new Date(result.extracted_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default VeridianCrawlerDashboard;

